import { UserManager, User, WebStorageStateStore } from 'oidc-client-ts'

export class OidcService {
	private userManager: UserManager
	private tokenRefreshTimer: number | null = null
	private user: User | null = null

	constructor(config: { isStaging: boolean; isLocal: boolean; keycloakUrl: string }) {
		// OIDC configuration
		const authority = config.keycloakUrl + '/auth/realms/MyServices'
		const clientId = 'contenthub-ui'
		const redirectUri = window.location.origin + window.location.pathname
		// offline_access scope is required to receive a refresh token
		const scope = 'openid profile email offline_access'

		this.userManager = new UserManager({
			authority,
			client_id: clientId,
			redirect_uri: redirectUri,
			post_logout_redirect_uri: redirectUri,
			response_type: 'code',
			scope,
			automaticSilentRenew: false, // We'll handle refresh manually with a timer
			userStore: new WebStorageStateStore({ store: window.localStorage }),
			// Additional settings
			loadUserInfo: true,
		})

		// Set up event handlers
		this.userManager.events.addUserLoaded((user) => {
			console.log('OIDC: User loaded', user)
			this.user = user
			this.scheduleTokenRefresh()
		})

		this.userManager.events.addAccessTokenExpiring(() => {
			console.log('OIDC: Access token expiring')
			this.refreshToken()
		})

		this.userManager.events.addAccessTokenExpired(() => {
			console.log('OIDC: Access token expired')
			this.clearTokenRefreshTimer()
		})

		this.userManager.events.addSilentRenewError((error) => {
			console.error('OIDC: Silent renew error', error)
		})

		this.userManager.events.addUserSignedOut(() => {
			console.log('OIDC: User signed out')
			this.clearTokenRefreshTimer()
		})
	}

	// Initialize OIDC and handle callback if present
	async initialize(): Promise<string | null> {
		try {
			// Check if we're returning from the OIDC provider (callback)
			if (window.location.search.includes('code=') || window.location.search.includes('state=')) {
				console.log('OIDC: Processing signin callback')
				this.user = await this.userManager.signinCallback()

				// Clean up the URL
				window.history.replaceState({}, document.title, window.location.pathname + window.location.hash)

				if (this.user && this.user.access_token) {
					this.scheduleTokenRefresh()
					return this.user.access_token
				}
			}

			// Try to get existing user from storage
			this.user = await this.userManager.getUser()

			if (this.user && !this.user.expired) {
				console.log('OIDC: Using existing valid token')
				this.scheduleTokenRefresh()
				return this.user.access_token
			}

			// No valid token found
			return null
		}
		catch (error) {
			console.error('OIDC: Initialization error', error)
			return null
		}
	}

	async login(): Promise<void> {
		try {
			await this.userManager.signinRedirect()
		}
		catch (error) {
			console.error('OIDC: Login error', error)
			throw error
		}
	}

	async logout(): Promise<void> {
		try {
			this.clearTokenRefreshTimer()
			await this.userManager.signoutRedirect()
		}
		catch (error) {
			console.error('OIDC: Logout error', error)
			throw error
		}
	}

	async getAccessToken(): Promise<string | null> {
		if (!this.user) {
			this.user = await this.userManager.getUser()
		}

		if (this.user && !this.user.expired) {
			return this.user.access_token
		}

		return null
	}

	private async refreshToken(): Promise<void> {
		try {
			console.log('OIDC: Refreshing token')
			this.user = await this.userManager.signinSilent()

			if (this.user && this.user.access_token) {
				console.log('OIDC: Token refreshed successfully')
				// Update localStorage token for backward compatibility
				window.localStorage.token = this.user.access_token
				this.scheduleTokenRefresh()
			}
		}
		catch (error) {
			console.error('OIDC: Token refresh failed', error)
			// If silent refresh fails, we might need to re-authenticate
			this.clearTokenRefreshTimer()
		}
	}

	private scheduleTokenRefresh(): void {
		this.clearTokenRefreshTimer()

		if (!this.user || !this.user.expires_at) {
			return
		}

		// Calculate time until token expires (in milliseconds)
		const now = Math.floor(Date.now() / 1000)
		const expiresIn = this.user.expires_at - now

		// Refresh 60 seconds before expiry (or immediately if less than 60 seconds)
		const refreshIn = Math.max(0, (expiresIn - 60) * 1000)

		console.log(`OIDC: Scheduling token refresh in ${Math.floor(refreshIn / 1000)} seconds`)

		this.tokenRefreshTimer = window.setTimeout(() => {
			this.refreshToken()
		}, refreshIn)
	}

	private clearTokenRefreshTimer(): void {
		if (this.tokenRefreshTimer !== null) {
			clearTimeout(this.tokenRefreshTimer)
			this.tokenRefreshTimer = null
		}
	}


	getUser(): User | null {
		return this.user
	}
}
